import {
  IconCheckmark,
  IconShieldCheck,
  IconXMark,
  Button,
  GreyGrey80,
  Primary13Regular,
  Box,
  Flex
} from '@sequencehq/core-components'
import {
  ExecutionSummaryComponent,
  ExecutionSummaryComponentProps
} from 'Automations/nodeDefinitions/types'
import {
  NodeTimeline,
  QuotePreviewCard,
  useNodeTimelineVariant
} from 'Automations/components'
import { ExpandableCard } from 'Automations/components/ExpandableCard'
import { useAllUsers } from 'common/hooks/useUsers'
import { useMemo } from 'react'
import { match } from 'ts-pattern'
import { AutomationExecutionTask, ReviewQuoteExecution } from './types'
import { useCurrentUser } from 'common/hooks'
import { useSetAtom } from 'jotai'
import {
  findReviewableTask,
  resolveUserEmails
} from 'Automations/nodeDefinitions/executionSummaryUtils'
import { manualTaskProcessorTaskIdAtom } from 'Automations/watchtower/atoms'
import { TASK_EXECUTORS } from 'Automations/automations.constants'

type ManualReviewOutcome = 'APPROVED' | 'REJECTED' | undefined
export const ReviewQuoteExecutionSummary: ExecutionSummaryComponent = ({
  tasks,
  reviewableTasks,
  isLatestNodeExecution
}) => {
  const showPreviews = useNodeTimelineVariant() === 'DEFAULT'
  const setProcessorTaskId = useSetAtom(manualTaskProcessorTaskIdAtom)
  const {
    quoteManualReviewTask,
    comment,
    visibleReviewers,
    reviewableTask,
    completedBy,
    quote,
    customer,
    requestorEmail,
    requestorIsCurrentUser
  } = useReviewQuoteData(tasks, reviewableTasks)

  return (
    <>
      <NodeTimeline.Item
        icon={getManualReviewIcon({
          outcome: quoteManualReviewTask?.outputData?.outcome,
          taskStatus: quoteManualReviewTask?.status || 'IN_PROGRESS'
        })}
        showStem={!isLatestNodeExecution}
      >
        <NodeTimeline.Title>
          {getManualReviewLabel({
            reviewer: completedBy ?? 'Unknown reviewer',
            allReviewers: visibleReviewers,
            taskStatus: quoteManualReviewTask?.status || 'IN_PROGRESS',
            startTime: quoteManualReviewTask?.startTime || '',
            outcome: quoteManualReviewTask?.outputData
              ?.outcome as ManualReviewOutcome,
            requestorEmail,
            requestorIsCurrentUser
          })}
        </NodeTimeline.Title>
        <Flex gap="8px" flexDirection="column">
          {!!comment && (
            <ExpandableCard>
              <Box
                {...Primary13Regular}
                color={GreyGrey80}
                whiteSpace="pre-wrap"
              >
                {comment}
              </Box>
            </ExpandableCard>
          )}
          {showPreviews && quote && customer && (
            <QuotePreviewCard
              quote={quote}
              customer={customer}
              startCollapsed={!isLatestNodeExecution && !reviewableTask}
              action={
                reviewableTask ? (
                  <Button
                    onClick={() => {
                      setProcessorTaskId(reviewableTask.id)
                    }}
                    variant="primary"
                    fullWidth
                    style={{
                      textDecoration: 'none'
                    }}
                  >
                    Review quote
                  </Button>
                ) : undefined
              }
            />
          )}
        </Flex>
      </NodeTimeline.Item>
    </>
  )
}

const getManualReviewIcon = ({
  outcome,
  taskStatus
}: {
  outcome: ManualReviewOutcome | undefined
  taskStatus: AutomationExecutionTask['status']
}) =>
  match({
    outcome,
    taskStatus
  })
    .with({ outcome: 'APPROVED' }, () => <IconCheckmark />)
    .with(
      { outcome: 'REJECTED' },
      { taskStatus: 'CANCELED' },
      { taskStatus: 'FAILED' },
      { taskStatus: 'FAILED_WITH_TERMINAL_ERROR' },
      () => <IconXMark />
    )
    .otherwise(() => <IconShieldCheck />)

const getManualReviewLabel = ({
  reviewer,
  allReviewers,
  startTime,
  outcome,
  requestorEmail,
  requestorIsCurrentUser,
  taskStatus
}: {
  reviewer: string
  allReviewers: Array<string>
  startTime: string
  outcome: ManualReviewOutcome
  requestorIsCurrentUser?: boolean
  requestorEmail?: string
  taskStatus: AutomationExecutionTask['status']
}) =>
  match({
    outcome,
    taskStatus
  })
    .with({ outcome: 'APPROVED' }, () => (
      <NodeTimeline.UserAction
        label="Quote approved"
        performedBy={reviewer}
        timestamp={startTime}
      />
    ))
    .with({ outcome: 'REJECTED' }, () => (
      <NodeTimeline.UserAction
        label="Quote rejected"
        performedBy={reviewer}
        timestamp={startTime}
      />
    ))
    .with(
      { taskStatus: 'CANCELED' },
      { taskStatus: 'FAILED' },
      { taskStatus: 'FAILED_WITH_TERMINAL_ERROR' },
      { taskStatus: 'TIMED_OUT' },
      () => (
        <NodeTimeline.UserAction
          label="Quote approval cancelled"
          timestamp={startTime}
        />
      )
    )
    .otherwise(() =>
      match(requestorIsCurrentUser)
        .with(true, () => (
          <NodeTimeline.UserAction
            label="Quote awaiting review"
            performedBy={allReviewers}
            timestamp={startTime}
          />
        ))
        .otherwise(() => (
          <NodeTimeline.UserAction
            label="Quote sent for approval"
            performedBy={requestorEmail}
            timestamp={startTime}
          />
        ))
    )

const useReviewQuoteData = (
  tasks: ExecutionSummaryComponentProps['tasks'],
  reviewableTasks: ExecutionSummaryComponentProps['reviewableTasks']
) => {
  const { users } = useAllUsers()
  const { id: currentUserId, email: currentUserEmail } = useCurrentUser()

  const quoteManualReviewTask = useMemo(() => {
    return tasks.find(
      task =>
        task.inputData.taskExecutorConfiguration?.executor ===
        TASK_EXECUTORS.reviewQuote
    ) as ReviewQuoteExecution | undefined
  }, [tasks])

  const allTasksCompleted = useMemo(() => {
    return tasks.every(task => task.status === 'COMPLETED')
  }, [tasks])

  const allReviewers = useMemo(() => {
    const assignedUserIds = (
      quoteManualReviewTask?.inputData.data.assignmentRules as {
        users: Array<string>
      }
    )?.users
    return resolveUserEmails(assignedUserIds, users)
  }, [quoteManualReviewTask, users])

  const reviewableTask = useMemo(
    () => findReviewableTask(tasks, reviewableTasks),
    [tasks, reviewableTasks]
  )

  const visibleReviewers = useMemo(
    () =>
      reviewableTask
        ? allReviewers
        : allReviewers.filter(email => email !== currentUserEmail),
    [allReviewers, currentUserEmail, reviewableTask]
  )

  const quote = useMemo(() => {
    return quoteManualReviewTask?.inputData.data.quote
  }, [quoteManualReviewTask])

  const customer = useMemo(() => {
    return quoteManualReviewTask?.inputData.data.customer
  }, [quoteManualReviewTask])

  const comment = useMemo(() => {
    return match(quoteManualReviewTask?.status)
      .with('COMPLETED', () => {
        return quoteManualReviewTask?.outputData?.comment
      })
      .with('CANCELED', () => {
        return ''
      })
      .otherwise(() => quoteManualReviewTask?.inputData.data.comment)
  }, [quoteManualReviewTask])

  const completedBy = useMemo(() => {
    return (
      quoteManualReviewTask?.outputData?.completedBy &&
      users.find(
        user => user.id === quoteManualReviewTask?.outputData?.completedBy
      )?.email
    )
  }, [quoteManualReviewTask, users])

  const showQuotePreview = !!(allTasksCompleted && quote && customer)

  const requestorEmail = useMemo(() => {
    return quoteManualReviewTask?.inputData.data.requestor?.email
  }, [quoteManualReviewTask])

  const requestorIsCurrentUser =
    quoteManualReviewTask?.inputData.data.requestor.id === currentUserId

  return {
    quoteManualReviewTask,
    allTasksCompleted,
    comment,
    visibleReviewers,
    reviewableTask,
    completedBy,
    quote,
    customer,
    showQuotePreview,
    requestorIsCurrentUser,
    requestorEmail
  }
}
